import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import './Legal.css';

export default function Terms() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="rh-legal-page">
      {/* Top bar */}
      <header className="rh-legal-topbar">
        <Link to="/" className="rh-legal-back">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          Back
        </Link>
        <Link to="/" className="rh-legal-brand">
          <img src={require('./assets/logo2.png')} alt="Reliable Handyman" className="rh-legal-brand-img" />
        </Link>
        <div className="rh-legal-spacer" aria-hidden="true" />
      </header>

      <main className="rh-legal-content">
        <header className="rh-legal-header">
          <div className="rh-legal-eyebrow">Legal</div>
          <h1>Terms of Service</h1>
          <p className="rh-legal-effective">Effective: June 4, 2026 · Last updated: June 10, 2026</p>
        </header>

        <section className="rh-legal-section">
          <p className="rh-legal-lede">
            These Terms of Service ("Terms") govern your use of the Reliable
            Handyman LLC website (getreliablehandyman.com) and any services we
            provide. By using our website or scheduling work with us, you agree
            to these Terms.
          </p>
        </section>

        <section className="rh-legal-section">
          <h2>1. Who we are</h2>
          <p>
            "Reliable Handyman," "we," "us," or "our" refers to Reliable Handyman
            LLC, a New York limited liability company located at 2417 Jericho
            Tpke, Garden City Park, NY 11040. Reliable Handyman is licensed and
            insured to perform home services on Long Island, primarily in
            Nassau County, with Suffolk County visits available on request.
          </p>
        </section>

        <section className="rh-legal-section">
          <h2>2. Eligibility</h2>
          <p>
            To use our services you must be at least 18 years old, the legal
            owner or authorized occupant of the property where work will be
            performed, and able to enter into a binding agreement. By submitting
            a service request you confirm that you meet these requirements.
          </p>
        </section>

        <section className="rh-legal-section">
          <h2>3. Service requests and estimates</h2>
          <p>
            Submitting a request through our website does not create a binding
            contract. We will respond with availability, an estimate, or
            confirmation of a scheduled service. A contract is formed only when
            we confirm your appointment or you accept a written estimate.
          </p>
          <p>
            <strong>Flat-rate services</strong> are priced as listed on our
            website at the time of booking. Flat-rate pricing assumes standard
            scope: existing rough-ins are functional, surfaces are accessible,
            and no unforeseen conditions exist. If we encounter conditions that
            materially change the scope (hidden damage, non-standard fixtures,
            etc.), we will stop work and provide a revised estimate before
            continuing.
          </p>
          <p>
            <strong>Hourly and estimated work</strong> is billed at the rate
            shown at the time of agreement ($100/hr) with a one-hour minimum.
            After the first hour, time is billed in whole-hour blocks. Discounts
            may apply to projects scheduled for 8 or more hours of labor.
          </p>
        </section>

        <section className="rh-legal-section">
          <h2>4. Materials</h2>
          <p>
            Unless otherwise stated, materials are billed at cost with
            photo-verified receipts. We do not mark up materials.
          </p>
          <p>
            For some flat-rate services, the customer provides the fixture or
            part to be installed (this is noted on the service tile). Reliable
            Handyman is not responsible for defects in customer-provided
            materials.
          </p>
        </section>

        <section className="rh-legal-section">
          <h2>5. Cancellation and rescheduling</h2>
          <p>
            You may cancel or reschedule a confirmed appointment by phone or
            email with at least 24 hours' notice without charge. Cancellations
            with less than 24 hours' notice may be subject to a fee equal to
            the minimum visit charge ($250) to cover lost dispatch time.
          </p>
          <p>
            If we cancel or reschedule due to weather, equipment failure, or
            other circumstances on our end, we will offer the next available
            slot at no charge.
          </p>
        </section>

        <section className="rh-legal-section">
          <h2>6. Payment</h2>
          <p>
            Payment is collected on-site after the work is complete and you
            confirm satisfaction. We accept credit card, cash, check, and
            common digital payment methods. We do not require deposits for
            standard flat-rate or hourly work; larger projects may require a
            deposit as specified in the written estimate.
          </p>
          <p>
            Past-due invoices accrue interest at the maximum rate permitted by
            New York law. If we have to engage collections or legal counsel to
            recover unpaid amounts, you agree to reimburse our reasonable
            collection costs and attorneys' fees.
          </p>
        </section>

        <section className="rh-legal-section">
          <h2>7. Warranty</h2>
          <p>
            We warrant our workmanship for 90 days from the date of completion.
            If work fails due to our error during that period, we will return
            and remedy the issue at no charge. This warranty does not cover:
          </p>
          <ul>
            <li>Defects in customer-provided materials or fixtures</li>
            <li>Damage caused by misuse, accident, or normal wear and tear</li>
            <li>Conditions outside the work we performed (e.g. pre-existing damage)</li>
            <li>Failures due to changes you or others made after our work was completed</li>
          </ul>
          <p>
            Manufacturer warranties on materials and fixtures are separate and
            apply per the manufacturer's terms.
          </p>
        </section>

        <section className="rh-legal-section">
          <h2>8. Property access and responsibility</h2>
          <p>
            You are responsible for ensuring that our technicians have safe and
            legal access to the work area at the agreed time. If we are unable
            to access the property at the scheduled time, the appointment may
            be treated as a cancellation under Section 5.
          </p>
          <p>
            You are responsible for clearing valuable, fragile, or sensitive
            items from the immediate work area before we arrive. We will
            exercise reasonable care, but Reliable Handyman is not liable for
            damage to items that should have been removed or protected.
          </p>
        </section>

        <section className="rh-legal-section">
          <h2>9. Limitation of liability</h2>
          <p>
            To the maximum extent permitted by law, Reliable Handyman LLC's
            total liability arising out of or related to any single job is
            limited to the amount you paid us for that job. We are not liable
            for indirect, incidental, consequential, or punitive damages
            (including lost profits, loss of use, or property devaluation),
            even if we have been advised of the possibility of such damages.
          </p>
          <p>
            Nothing in these Terms limits our liability for fraud, gross
            negligence, or any liability that cannot be limited by law.
          </p>
        </section>

        <section className="rh-legal-section">
          <h2>10. Insurance</h2>
          <p>
            Reliable Handyman LLC maintains general liability insurance and
            workers' compensation coverage as required by New York State law.
            Certificates of insurance are available upon written request.
          </p>
        </section>

        <section className="rh-legal-section">
          <h2>11. Use of website</h2>
          <p>
            You agree not to use our website for unlawful purposes, to interfere
            with its operation, or to access systems or data you are not
            authorized to access. We may suspend or terminate your access to
            the website at any time, with or without notice, for any reason.
          </p>
          <p>
            All content on our website (text, logos, photos, video, software)
            is owned by Reliable Handyman LLC or licensed to us. You may not
            copy, distribute, or create derivative works without written
            permission, except for personal, non-commercial use.
          </p>
        </section>

        <section className="rh-legal-section">
          <h2>12. SMS communications</h2>
          <p>
            By submitting a form on our site, you consent to receive transactional
            text messages from us related to your service request. Message and
            data rates may apply. You may opt out by replying STOP. See our{' '}
            <Link to="/privacy">Privacy Policy</Link> for details.
          </p>
        </section>

        <section className="rh-legal-section">
          <h2>13. Disputes</h2>
          <p>
            If a dispute arises, both parties agree to first attempt to resolve
            it informally by contacting us at{' '}
            <a href="mailto:help@getreliablehandyman.com">help@getreliablehandyman.com</a>.
          </p>
          <p>
            If we cannot resolve the dispute informally within 30 days, the
            dispute will be resolved by binding arbitration administered by the
            American Arbitration Association under its Consumer Arbitration
            Rules. Arbitration will take place in Nassau County, New York. Each
            party waives the right to a jury trial and the right to participate
            in any class action.
          </p>
          <p>
            Notwithstanding the above, either party may bring an individual
            action in small claims court for disputes within that court's
            jurisdiction.
          </p>
        </section>

        <section className="rh-legal-section">
          <h2>14. Governing law</h2>
          <p>
            These Terms are governed by the laws of the State of New York,
            without regard to its conflict of laws principles. Any legal action
            not subject to arbitration must be brought in the state or federal
            courts located in Nassau County, New York.
          </p>
        </section>

        <section className="rh-legal-section">
          <h2>15. Changes to these Terms</h2>
          <p>
            We may update these Terms from time to time. Updates take effect
            when posted to this page. If changes are material, we will make
            reasonable efforts to notify customers with active engagements.
            Continued use of our website or services after changes are posted
            constitutes acceptance.
          </p>
        </section>

        <section className="rh-legal-section">
          <h2>16. Contact us</h2>
          <p>For any questions about these Terms:</p>
          <ul>
            <li><a href="mailto:help@getreliablehandyman.com">help@getreliablehandyman.com</a></li>
            <li><a href="tel:+15163869923">(516) 386-9923</a></li>
            <li>Reliable Handyman LLC<br />2417 Jericho Tpke<br />Garden City Park, NY 11040</li>
          </ul>
        </section>

        <div className="rh-legal-footer-nav">
          <Link to="/privacy">View Privacy Policy →</Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="svc-marketing-footer">
        <div className="svc-container svc-marketing-footer-inner">
          <Link to="/" className="svc-marketing-footer-brand" style={{ textDecoration: 'none' }}>
            <img src={require('./assets/logo2.png')} alt="Reliable Handyman" style={{ height: 26 }} />
          </Link>
          <div className="svc-marketing-footer-contact">
            <a href="tel:+15163869923">(516) 386-9923</a>
            <a href="mailto:help@getreliablehandyman.com">help@getreliablehandyman.com</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
